"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import Lottie from "lottie-react"
import { Image } from "next/dist/client/image-component"
import { useEffect, useRef, useState } from "react"
import { Autoplay, Mousewheel, Pagination, Scrollbar } from "swiper/modules"
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react"
import { Desktop, Mobile, NewText } from "ui"
import VideoPauseIcon from "ui/icons/video/VideoPauseIcon"
import VideoPlayIcon from "ui/icons/video/VideoPlayIcon"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import * as animationData from "../../ui/LottieAnimations/Risho-Show.json"

const StyledSliderWrapper = styled.div<{ $stripeHeight: number }>`
  position: relative;
  width: 100vw;
  height: 100dvh;
  overflow: hidden;
  .swiper {
    height: 100%;
  }
  .swiper-slide {
    height: auto;
  }
  .swiper-pagination {
    bottom: ${(p) => p.$stripeHeight + 16}px !important;
    left: 50% !important;
    transform: translateX(-50%);
    width: fit-content !important;
    padding: 4px;
    background: white;
    border-radius: 200px;
    display: flex;
    gap: 8px;
    ${(props) => props.theme.breakpoints.up("md")} {
      bottom: 60px !important;
      padding: 6px 8px;
      gap: 16px;
    }
    .swiper-pagination-bullet {
      background: #cec2b8;
      opacity: 1;
      border-radius: 466.67px;
      height: 8px;
      width: 8px;
      transition: width 300ms ease-in-out;
      margin: 0 !important;
      ${(props) => props.theme.breakpoints.up("md")} {
        height: 14px;
        width: 14px;
      }
    }
    .swiper-pagination-bullet-active {
      width: 37px;
      background: #1d1d1b;
      ${(props) => props.theme.breakpoints.up("md")} {
        width: 65px;
      }
    }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 100%;
  }
`
const StyledImageWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`

const StyledTextWrapper = styled.div<{ $shiftDown?: boolean }>`
  position: absolute;
  top: ${(props) => (props.$shiftDown ? "100px" : "0")};
  left: 0;
  min-height: 453px;
  max-height: 100%;
  padding: 71px 63px;
  width: 100%;
  z-index: 9;
  p {
    margin: 0;
  }
  ${(props) => props.theme.breakpoints.up("sm")} {
    top: 5vw;
    padding: 0;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    /* padding: 0 7vw; */
    min-height: 100%;
  }
  @media screen and (min-width: 1900px) {
    /* padding: 0 10vw; */
  }
`
const BackgroundColorForDesktop = styled.div<{ $smallerMinWidth?: boolean }>`
  width: max-content;
  height: max-content;
  padding: 32px 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 328px;
  min-width: ${(props) => (props.$smallerMinWidth ? "848px" : "1000px")};
`
const StyledBottomText = styled(NewText)`
  position: absolute;
  bottom: 60px;
  left: 0;
  padding: 3px 20px 1px 28px;
  background: rgba(0, 0, 0, 0.8);
`

const StyledBottomTextWrapper = styled.div`
  padding: 12px 16px;
  p {
    margin: 0;
  }
`

const MobileFixedTop = styled.div`
  position: fixed;
  z-index: 1000;
  top: 0;
  inset-inline-start: 0;

  width: 100%;
  height: 77px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 16px;

  background-color: rgba(237, 237, 235, 0.8);

  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledLogo = styled.div`
  display: flex;
  width: 38px;
  height: 45px;
`

const StyledButton = styled(Button)`
  font-size: 18.79px;
  text-decoration: unset;
  width: fit-content;
  height: 32px;
  padding: 0px 18px;
  background-color: #000000;
  border: unset;
  color: #ffffff;
  border-radius: 12.65px;
  letter-spacing: -0.01em;
  :hover {
    background-color: #000000;
  }
`

const StyledAnimationWrapper = styled.div`
  --hhh: 171px;
  --space-from-edges: 56px;
  width: var(--hhh);
  height: var(--hhh);
  position: absolute;

  bottom: 20px;
  inset-inline-start: 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    --hhh: 361px;
    --space-from-edges: 0px;
    bottom: unset;
    inset-inline-start: unset;
    top: var(--space-from-edges);
    inset-inline-end: var(--space-from-edges);
  }
  @media screen and (min-width: 1440px) {
    --space-from-edges: 22px;
  }
  @media screen and (min-width: 1440px) {
    --space-from-edges: 13px;
  }
  z-index: 1000;
`

const StyledPlayButton = styled.div<{ $stripeHeight: number }>`
  --h: 32px;
  cursor: pointer;
  position: absolute;
  z-index: 10;
  inset-inline-end: 40px;
  width: var(--h);
  height: var(--h);
  bottom: ${(p) => p.$stripeHeight + 12}px;
  ${(props) => props.theme.breakpoints.up("md")} {
    bottom: 60px;
  }
  span {
    display: flex;
    width: 100%;
    height: 100%;
    position: relative;
    svg {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      min-width: 48px;
      width: 48px;
      height: 48px;
    }
  }
`

type Props = {
  locale: string
  data: any
  companyId?: number
  companyName?: string
  messages?: any
  scrollDownToForm: () => void
}

type SliderType = {
  imageForDesktop: string
  imageForMobile: string
  title: string
  description: string
  imageCredit: string
  backgroundColorForMobile: string
  whiteColor: boolean
  hideContentFromDesktop: boolean
}

export const LandingSlider: React.FC<Props> = ({ data, scrollDownToForm }: Props) => {
  const [swiper, setSwiper] = useState<SwiperClass>()
  const pageLogo = extractProperty(data, "pageLogo")
  const mobileTitle = extractProperty(data, "mobileTitle")

  const imageSlider: SliderType[] = extractProperty(data, "imageSkuder")?.map((image: any) => {
    const imageForDesktop = extractProperty(image, "imageForDesktop")
    const imageForMobile = extractProperty(image, "imageForMobile")
    const title = extractProperty(image, "title")
    const description = extractProperty(image, "description")
    const imageCredit = extractProperty(image, "imageCredit")
    const backgroundColorForMobile = extractProperty(image, "backgroundColorForMobile")
    const whiteColor = image?.properties?.find(
      (property: any) => property?.propertyName === "whiteColor",
    )?.propertyValue?.value
    const hideContentFromDesktop = image?.properties?.find(
      (property: any) => property?.propertyName === "hideContentFromDesktop",
    )?.propertyValue?.value

    return {
      imageForDesktop,
      imageForMobile,
      title,
      description,
      imageCredit,
      backgroundColorForMobile,
      whiteColor,
      hideContentFromDesktop,
    }
  })
  const formButton = extractProperty(data, "formButton")?.[0]?.name

  const bottomRefs = useRef<(HTMLDivElement | null)[]>([])
  const [bottomHeights, setBottomHeights] = useState<number[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  useEffect(() => {
    if (!bottomRefs.current) return

    const newHeights = bottomRefs.current.map((ref) => ref?.offsetHeight || 0)
    setBottomHeights(newHeights)
  }, [imageSlider.length])

  const handlePlayingTrue = () => {
    setIsPlaying(true)
  }
  const handlePlayingFalse = () => {
    setIsPlaying(false)
  }
  const pauseOrPlay = () => {
    if (!swiper) return
    if (isPlaying) {
      swiper.autoplay.stop()
    } else {
      swiper.autoplay.start()
    }
  }
  return (
    <StyledSliderWrapper $stripeHeight={bottomHeights[activeIndex]}>
      <Swiper
        style={{
          padding: 0,
          height: "100%",
          width: "100%",
        }}
        direction="horizontal"
        spaceBetween={0}
        modules={[Scrollbar, Mousewheel, Pagination, Autoplay]}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        scrollbar={{ draggable: true }}
        mousewheel={true}
        slidesPerView={1}
        initialSlide={0}
        onSwiper={(swiper) => setSwiper(swiper)}
        autoplay={{
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
          delay: 5000,
        }}
        onAutoplayStart={handlePlayingTrue}
        onAutoplayStop={handlePlayingFalse}
        pagination={{
          clickable: true,
          renderBullet: (index, className) => {
            return `<span class="${className}"></span>`
          },
        }}
      >
        {imageSlider.map((item: SliderType, key: number) => (
          <SwiperSlide
            key={key.toString()}
            style={{
              width: "100%",
            }}
          >
            <Desktop sx={{ height: "100%" }}>
              <StyledImageWrapper>
                {key === 0 && (
                  <StyledAnimationWrapper>
                    <NewText
                      component={"div"}
                      desktopFontSize={34}
                      desktopFontWeight={700}
                      desktopLineHeight={"29px"}
                      letterSpacing={"-0.6px"}
                      color="black"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%) rotate(-9.361deg)",
                        zIndex: 10,
                        maxWidth: "241px",
                        textAlign: "center",
                        height: "max-content",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 34,
                          lineHeight: "29px",
                          letterSpacing: "-0.68px",
                        }}
                      >
                        משרדים
                        <br />
                        להשכרה
                        <br />
                      </div>
                      <div
                        style={{
                          color: "white",
                          fontSize: 35,
                          letterSpacing: "-0.7px",
                        }}
                      >
                        בניין C
                        <br />
                        השיווק החל!
                      </div>
                      <div
                        style={{
                          letterSpacing: "-0.6px",
                          lineHeight: "120%",
                          fontSize: 30,
                        }}
                      >
                        אכלוס
                        <br />- 2026 -
                      </div>
                    </NewText>
                    <Lottie
                      // @ts-ignore types may differ when dynamically importing
                      animationData={animationData}
                      loop={3}
                      style={{ width: "var(--hhh)", height: "var(--hhh)" }}
                    />
                  </StyledAnimationWrapper>
                )}
                {!item.hideContentFromDesktop && (
                  <StyledTextWrapper $shiftDown={key === 2}>
                    <BackgroundColorForDesktop
                      style={{ backgroundColor: `#${item.backgroundColorForMobile}` }}
                      $smallerMinWidth={key === 2}
                    >
                      <NewText
                        component={"h2"}
                        desktopFontSize={60}
                        desktopFontWeight={700}
                        desktopLineHeight={"110%"}
                        letterSpacing={"-0.01em"}
                        color="white"
                      >
                        {item.title}
                      </NewText>
                      <NewText
                        component={"p"}
                        desktopFontSize={60}
                        desktopFontWeight={500}
                        desktopLineHeight={"110%"}
                        letterSpacing={"-0.02em"}
                        color="white"
                        dangerouslySetInnerHTML={{
                          __html: item.description,
                        }}
                      />
                    </BackgroundColorForDesktop>
                  </StyledTextWrapper>
                )}
                <Image
                  src={getUmbracoMediaUrl(item?.imageForDesktop)}
                  fill
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                    objectPosition: "top",
                  }}
                  alt={item.imageCredit}
                  priority={key === 0}
                />
                <StyledBottomText
                  component={"span"}
                  desktopFontSize={14}
                  desktopLineHeight={"13px"}
                  desktopFontWeight={600}
                  letterSpacing={"0"}
                  color="white"
                >
                  הדמיות להמחשה בלבד. טל.ח.
                </StyledBottomText>
              </StyledImageWrapper>
            </Desktop>
            <Mobile sx={{ height: "100dvh" }}>
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    height: `calc(100% - ${bottomHeights[key] || 0}px)`,
                    transition: "height 0.3s ease",
                    position: "relative",
                  }}
                >
                  <Image
                    src={getUmbracoMediaUrl(item?.imageForMobile)}
                    fill
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "top",
                    }}
                    alt={item.imageCredit}
                    priority={key === 0}
                  />
                  {key === 0 && (
                    <StyledAnimationWrapper>
                      <NewText
                        component={"div"}
                        desktopFontSize={34}
                        desktopFontWeight={700}
                        desktopLineHeight={"29px"}
                        letterSpacing={"-0.6px"}
                        color="black"
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%) rotate(-9.361deg)",
                          zIndex: 10,
                          maxWidth: "241px",
                          textAlign: "center",
                          height: "max-content",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 14,
                            lineHeight: "15px",
                            letterSpacing: "-0.28px",
                          }}
                        >
                          משרדים
                          <br />
                          להשכרה
                          <br />
                        </div>
                        <div
                          style={{
                            color: "white",
                            fontSize: 14,
                            lineHeight: "100%",
                            letterSpacing: "-0.28px",
                          }}
                        >
                          בניין C
                          <br />
                          השיווק החל!
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            letterSpacing: "-0.28px",
                            lineHeight: "120%",
                          }}
                        >
                          אכלוס
                          <br />- 2026 -
                        </div>
                      </NewText>
                      <Lottie
                        // @ts-ignore types may differ when dynamically importing
                        animationData={animationData}
                        loop={3}
                        style={{ width: "var(--hhh)", height: "var(--hhh)" }}
                      />
                    </StyledAnimationWrapper>
                  )}
                </Box>
                <StyledBottomTextWrapper
                  ref={(el) => (bottomRefs.current[key] = el)}
                  style={{ backgroundColor: `#${item.backgroundColorForMobile}` }}
                >
                  {(item.title || item.description) && (
                    <Box
                      marginBottom={"16px"}
                      // maxWidth={"273px"}
                      sx={{
                        color: item.whiteColor ? "#ffffff" : "#000000",
                      }}
                    >
                      {mobileTitle && (
                        <NewText
                          component={"h2"}
                          desktopFontSize={25.79}
                          desktopFontWeight={700}
                          desktopLineHeight={"22.2px"}
                          mobileFontSize={30}
                          mobileLineHeight={"31px"}
                          letterSpacing={{
                            xs: "-0.03em",
                            md: "-0.05em",
                          }}
                          mb={{
                            xs: "16px",
                          }}
                          inheritColor
                        >
                          {mobileTitle}
                        </NewText>
                      )}
                      <Box marginBottom={"16px"}>
                        {item.title && (
                          <NewText
                            component={"p"}
                            desktopFontSize={25.79}
                            desktopFontWeight={700}
                            desktopLineHeight={"110%"}
                            letterSpacing={"-0.129px"}
                            inheritColor
                          >
                            {item.title}
                          </NewText>
                        )}
                        {item.description && (
                          <NewText
                            component={"p"}
                            desktopFontSize={25.79}
                            desktopFontWeight={500}
                            desktopLineHeight={"22.2px"}
                            letterSpacing={"-0.05em"}
                            inheritColor
                            dangerouslySetInnerHTML={{
                              __html: item.description,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </StyledBottomTextWrapper>
              </Box>
            </Mobile>
          </SwiperSlide>
        ))}
      </Swiper>
      <MobileFixedTop>
        <StyledLogo>
          <Image
            src={getUmbracoMediaUrl(pageLogo)}
            alt="page logo"
            width={500}
            height={500}
            style={{
              height: "100%",
              width: "100%",
              objectFit: "cover",
            }}
            priority={true}
          />
        </StyledLogo>
        <StyledButton variant="contained" onClick={scrollDownToForm}>
          {formButton}
        </StyledButton>
      </MobileFixedTop>
      <StyledPlayButton onClick={pauseOrPlay} $stripeHeight={bottomHeights[activeIndex]}>
        {isPlaying ? (
          <VideoPauseIcon width={48} height={48} />
        ) : (
          <VideoPlayIcon width={48} height={48} />
        )}
      </StyledPlayButton>
    </StyledSliderWrapper>
  )
}

export default LandingSlider
